import { ApiError, requestJson, type ApiEnvelope } from '@shared/api/http';
import type { ContactDirectoryUser, FriendRequest, UserSearchResult } from './types';

function ensureSuccess<T>(body: ApiEnvelope<T>, fallbackMessage: string): ApiEnvelope<T> {
  if (body.status && body.status !== 'Success') {
    throw new ApiError(body.message ?? fallbackMessage, body.statusCode ?? 500, body);
  }
  return body;
}

function withQuery(path: string, query: Record<string, string | number | undefined>) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  }
  const suffix = params.toString();
  return suffix ? `${path}?${suffix}` : path;
}

export async function searchUsers(
  keyword: string,
  baseUrl?: string,
  page = 1,
  limit = 20,
): Promise<UserSearchResult[]> {
  const body = await requestJson<ApiEnvelope<Record<string, unknown>>>(
    withQuery('/user/search', {
      keyword,
      page,
      limit,
    }),
    { baseUrl },
  );

  const success = ensureSuccess(body, 'Search failed');
  const payload = success.data ?? {};
  const rawUsers = Array.isArray(payload.users) ? payload.users : [];

  return rawUsers.map((entry) => {
    const user = entry as Record<string, unknown>;
    return {
      id: Number(user.id ?? 0),
      name: String(user.name ?? ''),
      email: String(user.email ?? ''),
      avatarUrl: String(user.avatar ?? ''),
      status: String(user.status ?? ''),
    };
  });
}

export async function sendFriendRequest(
  toUserId: number,
  verifyMessage: string,
  baseUrl?: string,
): Promise<void> {
  const body = await requestJson<ApiEnvelope<unknown>>('/friend-request/send', {
    method: 'POST',
    baseUrl,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to_user_id: toUserId,
      ...(verifyMessage.trim() ? { verify_message: verifyMessage.trim() } : {}),
    }),
  });
  ensureSuccess(body, 'Failed to send friend request');
}

export async function fetchReceivedFriendRequests(
  baseUrl?: string,
  page = 1,
  limit = 20,
  status = 0,
): Promise<FriendRequest[]> {
  let body: ApiEnvelope<Record<string, unknown>>;
  try {
    body = await requestJson<ApiEnvelope<Record<string, unknown>>>(
      withQuery('/friend-request/received', {
        page,
        limit,
        status,
      }),
      { baseUrl },
    );
  } catch (error) {
    // Some backend deployments reject `status=0`; fall back to the default list endpoint.
    body = await requestJson<ApiEnvelope<Record<string, unknown>>>(
      withQuery('/friend-request/received', {
        page,
        limit,
      }),
      { baseUrl },
    );
  }

  const success = ensureSuccess(body, 'Failed to load friend requests');
  const payload = success.data ?? {};
  const rawList =
    (Array.isArray(payload.requests) && payload.requests) ||
    (Array.isArray(payload.content) && payload.content) ||
    (Array.isArray(payload.data) && payload.data) ||
    (Array.isArray(payload.list) && payload.list) ||
    [];

  return rawList.map((entry) => {
    const request = entry as Record<string, unknown>;
    const statusValue = request.statusCode ?? request.status ?? 0;
    return {
      requestId: Number(request.requestId ?? request.request_id ?? request.id ?? 0),
      fromUserId: Number(
        request.fromUserId
        ?? request.from_user_id
        ?? ((request.fromUser as Record<string, unknown> | undefined)?.id)
        ?? 0,
      ),
      fromUserName: String(
        request.fromUserName
        ?? request.from_user_name
        ?? ((request.fromUser as Record<string, unknown> | undefined)?.name)
        ?? '',
      ),
      fromUserNick: String(
        request.fromUserNick
        ?? request.from_user_nick
        ?? ((request.fromUser as Record<string, unknown> | undefined)?.nick)
        ?? '',
      ),
      fromUserAvatar: String(
        request.fromUserAvatar
        ?? request.from_user_avatar
        ?? ((request.fromUser as Record<string, unknown> | undefined)?.avatar)
        ?? '',
      ),
      fromUserEmail: String(
        request.fromUserEmail
        ?? request.from_user_email
        ?? ((request.fromUser as Record<string, unknown> | undefined)?.email)
        ?? '',
      ),
      verifyMessage: String(request.verifyMessage ?? request.verify_message ?? ''),
      status: Number(typeof statusValue === 'string' ? 0 : statusValue),
      created: Number(request.created ?? 0),
    };
  });
}

export async function approveFriendRequest(requestId: number, baseUrl?: string): Promise<void> {
  const body = await requestJson<ApiEnvelope<unknown>>(`/friend-request/approve/${requestId}`, {
    method: 'POST',
    baseUrl,
  });
  ensureSuccess(body, 'Failed to approve friend request');
}

export async function rejectFriendRequest(requestId: number, baseUrl?: string): Promise<void> {
  const body = await requestJson<ApiEnvelope<unknown>>(`/friend-request/reject/${requestId}`, {
    method: 'POST',
    baseUrl,
  });
  ensureSuccess(body, 'Failed to reject friend request');
}

export async function fetchPendingRequestCount(baseUrl?: string): Promise<number> {
  const body = await requestJson<ApiEnvelope<Record<string, unknown>>>('/friend-request/count', {
    baseUrl,
  });
  const success = ensureSuccess(body, 'Failed to load pending request count');
  return Number(success.data?.count ?? 0);
}

/**
 * Loads the complete authoritative friend-ID set without importing presentation
 * fields into relationship state. Profile names and media are hydrated through
 * the bounded canonical public-summary endpoint by IdentityCoordinator.
 */
export async function fetchAllFriendIds(baseUrl?: string): Promise<number[]> {
  const limit = 200;
  const ids = new Set<number>();
  for (let page = 1; ; page += 1) {
    const body = await requestJson<ApiEnvelope<Record<string, unknown>>>(
      withQuery('/friend', { page, limit }),
      { baseUrl },
    );
    const success = ensureSuccess(body, 'Failed to load friends');
    const payload = success.data ?? {};
    const content = Array.isArray(payload.content) ? payload.content : [];
    for (const entry of content) {
      if (!entry || typeof entry !== 'object') continue;
      const userId = Number((entry as Record<string, unknown>).userId ?? 0);
      if (Number.isSafeInteger(userId) && userId > 0) ids.add(userId);
    }
    const pagination = payload.pagination && typeof payload.pagination === 'object'
      ? payload.pagination as Record<string, unknown>
      : {};
    const total = Number(pagination.total ?? ids.size);
    if (content.length < limit || ids.size >= total) break;
  }
  return [...ids].sort((left, right) => left - right);
}

export async function fetchUserById(userId: number, baseUrl?: string): Promise<ContactDirectoryUser> {
  const body = await requestJson<ApiEnvelope<Record<string, unknown>>>(`/user/${userId}`, { baseUrl });
  const success = ensureSuccess(body, 'Failed to fetch user details');
  const user = success.data ?? {};
  const avatar = user.avatar && typeof user.avatar === 'object'
    ? user.avatar as Record<string, unknown>
    : null;
  const homepageBackground = user.homepageBackground && typeof user.homepageBackground === 'object'
    ? user.homepageBackground as Record<string, unknown>
    : null;
  const homepageBackgroundUrl = String(homepageBackground?.url ?? '').trim();
  return {
    id: Number(user.userId ?? 0),
    name: String(user.displayName ?? ''),
    // This compatibility view remains until relation UI reads IdentityStore
    // directly. Public profile responses never hydrate private account data.
    email: '',
    phone: '',
    avatarUrl: String(avatar?.url ?? ''),
    signInfo: String(user.signature ?? ''),
    status: 0,
    homepageBackground: homepageBackgroundUrl
      ? { provider: 's3', url: homepageBackgroundUrl }
      : null,
    profileVersion: Number(user.profileVersion ?? 0),
  };
}
